import { useState } from "react";
import { CheckIcon, DownloadIcon, UploadIcon } from "./icons";
import { docFileUrl, downloadFile } from "../utils/download";
import { resolveImage } from "../data/assetMap";
import Spinner from "./Spinner";

const thumbStyle = (d) => ({
  backgroundColor: d.kind === "PDF" ? "#FDECEC" : "#E7F1FB",
  backgroundImage: `url("${resolveImage(d.prevUrl || d.prev)}")`,
});

// Send to Bank's per-financier documents, laid out like the Upload Documents
// step: one accordion per category, each doc type with its Required /
// Optional flag, upload status, the files uploaded against it (each with an
// include-in-send checkbox) and an upload control until the doc type's limit
// (cap) is reached. Files can't be removed here; readOnly (the bank has been
// sent to) hides the upload controls too.
// Required doc types with nothing uploaded are flagged — sending stays
// blocked until they are.
export default function SendDocsChecklist({ cats, isIncluded, onToggleInclude, onPreview, onUpload, uploadingId, readOnly }) {
  const [closed, setClosed] = useState({});
  const toggle = (key) => setClosed((st) => ({ ...st, [key]: !st[key] }));

  if (!cats.length) return <div className="muted-copy">No documents are listed for this bank.</div>;

  return (
    <div>
      {cats.map((c) => {
        const uploaded = c.reqs.filter((r) => r.files.length > 0).length;
        const pendingRequired = c.reqs.filter((r) => r.required && r.files.length === 0).length;
        const open = !closed[c.key];
        return (
          <div className="accordion" key={c.key}>
            <button type="button" className="accordion__toggle" onClick={() => toggle(c.key)}>
              <span className="accordion__titles">
                <span className="accordion__title">{c.name}</span>
                <span className="accordion__sub">
                  {pendingRequired > 0 ? pendingRequired + " required pending" : "All required uploaded"}
                </span>
              </span>
              <span className="accordion__meta">
                <span className={"accordion__badge" + (pendingRequired === 0 ? " accordion__badge--done" : "")}>
                  {uploaded}/{c.reqs.length}
                </span>
                <span className="accordion__sign">{open ? "−" : "+"}</span>
              </span>
            </button>
            {open && (
              <div className="req-list">
                {c.reqs.map((r) => {
                  const has = r.files.length > 0;
                  const missing = r.required && !has;
                  const uploading = uploadingId === r.id;
                  return (
                    <div key={r.id} className={"req-row" + (missing ? " req-row--missing" : "")}>
                      <div className="req-row__head">
                        <span className={"req-row__tick" + (has ? " req-row__tick--on" : "")}>
                          {has && <CheckIcon size={11} stroke="#fff" width={3} />}
                        </span>
                        <span className="req-row__label" style={{ color: has ? "#1F1F1F" : "#444444" }}>
                          {r.name}
                        </span>
                        <span
                          className="req-row__count"
                          style={r.required ? { background: "#FFF3D6", color: "#9A6700" } : { background: "var(--ink-050)", color: "var(--ink-500)" }}
                        >
                          {r.required ? "Required" : "Optional"}
                        </span>
                        <span
                          className="req-row__count"
                          style={{
                            background: has ? "var(--success-bg)" : missing ? "var(--danger-bg)" : "var(--ink-050)",
                            color: has ? "var(--success)" : missing ? "var(--danger)" : "var(--ink-500)",
                          }}
                        >
                          {has ? "Uploaded" + (r.files.length > 1 ? " · " + r.files.length : "") : "Not uploaded"}
                        </span>
                      </div>
                      {missing && <div className="req-row__error">Upload this document to send to the bank</div>}

                      {r.files.map((d) => {
                        const inc = isIncluded(d);
                        return (
                          <div key={d.id} className={"req-doc-row" + (!inc ? " docs-attached__row--excluded" : "")}>
                            <button
                              type="button"
                              className={"docs-attached__check" + (inc ? " docs-attached__check--on" : "")}
                              aria-label="Include in send"
                              onClick={() => onToggleInclude(d)}
                            >
                              {inc && <CheckIcon size={12} stroke="#fff" width={3} />}
                            </button>
                            <span className="req-doc-row__thumb" style={thumbStyle(d)} />
                            <span style={{ flex: "1 1 150px", minWidth: 0 }}>
                              <span className={"req-doc-row__name" + (!inc ? " docs-attached__name--excluded" : "")}>{d.name}</span>
                              <span className="req-doc-row__meta">
                                {inc ? "Included in email" : "Not included in email"}
                                {d.docType ? " · " + d.docType.charAt(0).toUpperCase() + d.docType.slice(1) : ""}
                              </span>
                            </span>
                            <span className="req-doc-row__actions">
                              <button type="button" className="btn-preview" onClick={() => onPreview(d)}>
                                View
                              </button>
                              {docFileUrl(d) && (
                                <button
                                  type="button"
                                  className="btn-preview btn-download"
                                  aria-label={"Download " + d.name}
                                  onClick={() => downloadFile(docFileUrl(d), d.name)}
                                >
                                  <DownloadIcon size={13} />
                                  Download
                                </button>
                              )}
                            </span>
                          </div>
                        );
                      })}

                      {!readOnly && !r.editable && (
                        <div className="req-row__full">
                          {has ? "Manage" : "Upload"} this from the Upload Documents step of the application
                        </div>
                      )}
                      {!readOnly && r.editable && r.files.length < r.cap && (
                        <button type="button" className="req-row__drop" onClick={() => onUpload(r)} disabled={!!uploadingId}>
                          {uploading ? <Spinner /> : <UploadIcon size={15} />}
                          <span>{uploading ? "Uploading…" : has ? "Add another file (" + r.files.length + "/" + r.cap + ")" : "Upload a file"}</span>
                        </button>
                      )}
                      {!readOnly && r.editable && r.files.length >= r.cap && (
                        <div className="req-row__full">
                          {r.cap === 1 ? "Already uploaded" : "Maximum " + r.cap + " files uploaded"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
